/**
 * SOLID Review
 * S — Good. One concern, the client's table: every rule that moves it — the local player's moves, the server's
 *     answers and events, the catch-up reads — and what the screen reads off it. What depends on the phase is its
 *     State's (`TablePhase`); seats, lives, the reveal and the claim are value objects.
 * O — Good. A new move or event is one method here; a new phase-dependent rule is one question on `TablePhase`.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Good. One method per decision the table takes, the four writes a seat taken makes, the queries and the
 *     Memento pair; a decision calls one.
 * D — Strong. Its State and value objects, the server's dice ranking, the refusal envelope and the schema types.
 *     No store, no port.
 */
import { GamePhase } from '../uml-components/primitives';
import { allowed, refuse, REFUSAL, type TDecisionResult } from '../decisions/shared';
import { sameJson } from '../../shared/sameJson';
import { TablePhase, type TCupAction } from './TablePhase';
import { Roster } from './Roster';
import { TableLives } from './TableLives';
import { Reveal } from './Reveal';
import { Claim } from './Claim';
import type { TableRoom } from './TableRoom';
import type { DiceRanking } from '../dice/DiceRanking';
import type { IStateClientTable, IEntityClientTableDerived, IEntityClientClaimOption } from '../ports/schemas/ClientTable';
import type { IEntityClientDiceRank } from '../ports/schemas/DiceRankingMoves';
import type { IEntityClientResume } from '../ports/schemas/ClientSession';
import type {
  IPayloadRoundStarted, IPayloadClaimAnnounced, IPayloadCupLifted, IPayloadDiceRolled, IPayloadRoomRoster, IPayloadPresence,
  IPayloadRoomAbandoned, IPayloadDecisionCountdown, IPayloadTableView, IPayloadActiveRound, IEntityTableViewOutcome,
} from '../ports/schemas/TableEvents';
import type {
  ICommandGameStarted, ICommandRoundMoveAnswered, ICommandPeeked, ICommandClaimAccepted, ICommandBelieveAccepted,
  ICommandPlayAgainAnswered, ICommandActiveRoundRestored, ICommandSuspendedReconciled, ICommandLobbyActiveRound,
  ICommandLobbyRoomLoaded, IPayloadStartGame, IPayloadRoundMove, IPayloadAnnounce, IPayloadBelieve, IPayloadPlayAgain,
  IPayloadResync, IPayloadRoomRead, IEntityLeave,
} from '../ports/schemas/TableMoves';

/** The table as the store starts it, and as `resetAll` restores it: no identity, no room, IDLE. */
const EMPTY_TABLE: IStateClientTable = {
  localPlayerId: null,
  room: null,
  phase: GamePhase.IDLE,
  activeRoundId: null,
  activeRollerId: null,
  activeChallengerId: null,
  lastDiceRoll: null,
  minimumClaimScore: 0,
  lastClaimedScore: null,
  claimWasBlind: false,
  diceRevealed: false,
  opponentClaim: null,
  revealResult: null,
  decisionCountdown: null,
  playerLives: {},
  suspendedPlayerIds: [],
  roomEnded: false,
  tableVersion: null,
  connectionState: 'disconnected',
  connectionWasDown: false,
  lastGameActivityAt: 0,
};

type TConnectionState = IStateClientTable['connectionState'];
/**
 * The transport's states the table names as they are; any other — 'disconnected' included — reads as
 * disconnected. Frozen arrays, not a Map and a Set (strict-ports FE7): `lint:module-state` refuses a
 * module-level collection in the Domain, which runs on the far side of a worker in FE8's proof.
 */
const MAPPED_STATES: readonly TConnectionState[] = Object.freeze(['connecting', 'connected', 'reconnecting', 'failed']);
const DOWN_STATES: readonly string[] = Object.freeze(['reconnecting', 'disconnected', 'failed']);
const isMapped = (state: string): state is TConnectionState => (MAPPED_STATES as readonly string[]).includes(state);

/**
 * THE CLIENT'S TABLE, AS AN AGGREGATE (frontend-domain-oo P3, 2026-10-26; handbook §28.270). Until P3 the table was a
 * plain record: 34 decide functions copied it, edited the copy field by field and handed it to `settle`
 * (`decisions/table/`), with the phase tested in six files and the guard helpers (`seatedAs`, `notReady`,
 * `cupMove`) beside each group. The rules are the aggregate's now. A decision rebuilds it from the snapshot, calls
 * one method and settles the snapshot it answers — P5 turns those decisions into the context services.
 *
 * Memento: `fromSnapshot` / `toSnapshot`, the `IStateClientTable` the port carries. The phase is a State object
 * (`TablePhase`, one class per `GamePhase`), asked every question whose answer depends on it; the snapshot's `phase`
 * stays the discriminator. Seats, lives, a reveal and the claim on the table are value objects (`Roster`,
 * `TableLives`, `Reveal`, `Claim`).
 *
 * Three kinds of method, as the decisions they serve:
 *   - a PLAN (`plan*`) answers what to send, or the refusal: where the handler it replaced returned silently, the
 *     plan refuses with a code. A plan may move the table before the call (the lift, Play Again);
 *   - an ANSWER or an EVENT answers whether it applied: one the guards drop changes nothing;
 *   - a QUERY (`derived`, `resume`, `storedGameSince`) reads the table.
 *
 * Nested values (the room, the lives) are REPLACED, never edited in place: the snapshot a table is built from is
 * the caller's, and the empty table's are shared.
 */
export class ClientTable {
  private constructor(private table: IStateClientTable) {}

  /** The table a snapshot holds — a copy: the snapshot itself is never written. */
  static fromSnapshot(snapshot: IStateClientTable): ClientTable {
    const table: IStateClientTable = { ...snapshot };
    return new ClientTable(table);
  }

  /** The table as the store starts it, and as `resetAll` restores it: no identity, no room, IDLE. */
  static empty(): ClientTable {
    const table: IStateClientTable = { ...EMPTY_TABLE };
    return new ClientTable(table);
  }

  toSnapshot(): IStateClientTable {
    return { ...this.table };
  }

  /** The table's phase, as its State. */
  private get phase(): TablePhase {
    return TablePhase.of(this.table.phase);
  }

  private enter(phase: GamePhase): void {
    this.table.phase = phase;
  }

  // ── Guards ─────────────────────────────────────────────────────────────────────────────────────────────

  /**
   * Still at a table, as the player who sent the request. THE ONE GUARD EVERY ANSWER SHARES (FE1): an answer that
   * arrives after the player has left the table is ignored. The handlers wrote it into whatever the store held by
   * then, which after `resetAll` was a table the player had already left (deviation, flagged in §28). No answer is
   * checked against the round it was sent for: the server's round-start event may arrive first and move the round
   * on, and the answer must still apply exactly as the handler applied it.
   */
  private seatsAs(playerId: string): boolean {
    return this.table.room !== null && this.table.localPlayerId === playerId;
  }

  /** At the table of room `roomId` — a guard of the table's, and of the session's restore. */
  sitsAt(roomId: string | null | undefined): boolean {
    return this.table.room !== null && this.table.room.roomId === roomId;
  }

  /** On round `roundId` — an event for another round of this room is stale and dropped. */
  private isOnRound(roundId: string): boolean {
    return this.table.activeRoundId !== null && roundId === this.table.activeRoundId;
  }

  /** This tab rolls. */
  private isLocalRoller(): boolean {
    return this.table.localPlayerId !== null && this.table.localPlayerId === this.table.activeRollerId;
  }

  private static notReady<R>(what: string): TDecisionResult<R> {
    return refuse(`The table is not ready: ${what}.`, REFUSAL.NOT_READY);
  }

  /** The server's decider as the store held it: `setActiveChallengerId` turned '' into null. */
  private adoptDecider(id: string | null | undefined): void {
    this.table.activeChallengerId = id || null;
  }

  /** The seats, in join order — none without a room. */
  private seats(): Roster {
    return Roster.of(this.table.room?.players ?? []);
  }

  /** Every seat back to full lives. */
  private restoreLives(): void {
    this.table.playerLives = TableLives.full(this.seats().ids()).toRecord();
  }

  /** The claim this client is asked to believe or lift, if any. */
  private claim(): Claim | null {
    return this.table.opponentClaim === null ? null : Claim.of(this.table.opponentClaim);
  }

  // ── The local player's moves: LobbyPanel's Start, GameSceneCore's cup tap, ControlPanel's buttons ──────────

  /** Start (LobbyPanel): a room and an identity. */
  planStartGame(): TDecisionResult<IPayloadStartGame> {
    const { room, localPlayerId } = this.table;
    if (room === null || localPlayerId === null) return ClientTable.notReady('no room or no identity');
    const request: IPayloadStartGame = { roomId: room.roomId, playerId: localPlayerId };
    return allowed(request);
  }

  /**
   * The host rolls first and moves to ROLLING on the answer, without waiting for the round's start event: without
   * the round id the cup tap bailed out, the "sometimes can't roll on game start" race.
   */
  gameStarted(command: ICommandGameStarted): boolean {
    const { request, answer } = command;
    if (!this.seatsAs(request.playerId) || !this.sitsAt(request.roomId)) return false;
    if (answer.roundId) this.table.activeRoundId = answer.roundId;
    this.table.activeRollerId = answer.rollerId ?? request.playerId;
    this.enter(GamePhase.ROLLING);
    return true;
  }

  /** First tap, in ROLLING: roll. The server keeps the dice secret until the roller peeks. */
  planRoll(): TDecisionResult<IPayloadRoundMove> {
    return this.cupMove('roll');
  }

  rolled(command: ICommandRoundMoveAnswered): boolean {
    if (!this.seatsAs(command.request.playerId)) return false;
    this.table.diceRevealed = false;
    this.enter(GamePhase.PEEKING);
    return true;
  }

  /** Second tap, in PEEKING with the dice still hidden: ask the server for them. */
  planPeek(): TDecisionResult<IPayloadRoundMove> {
    return this.cupMove('peek');
  }

  peeked(command: ICommandPeeked): boolean {
    if (!this.seatsAs(command.request.playerId)) return false;
    this.table.lastDiceRoll = command.answer;
    this.table.diceRevealed = true;
    return true;
  }

  /** The cup: only the roller touches it, with a round, and only as the phase's cup action allows. */
  private cupMove(move: NonNullable<TCupAction>): TDecisionResult<IPayloadRoundMove> {
    const { localPlayerId: me, activeRoundId, diceRevealed } = this.table;
    if (me === null || activeRoundId === null) return ClientTable.notReady('no round or no identity');
    if (me !== this.table.activeRollerId) return refuse('Only the roller may touch the cup.', REFUSAL.NOT_YOUR_MOVE);
    if (this.phase.cupAction(diceRevealed) !== move) {
      return refuse(`The cup does nothing in ${this.table.phase}${diceRevealed ? ' with the dice seen' : ''}.`, REFUSAL.NOT_YOUR_MOVE);
    }
    const request: IPayloadRoundMove = { roundId: activeRoundId, playerId: me };
    return allowed(request);
  }

  /**
   * Claim: two endpoints for one rule, chosen by whether these dice have been seen here — an open claim, or a blind
   * pass. The server owns the verdict either way: it refuses a blind pass once it has recorded a peek, so a client
   * that lost track of its own state is told rather than silently downgraded (§28.62).
   */
  planAnnounce(score: number): TDecisionResult<IPayloadAnnounce> {
    const { activeRoundId, localPlayerId } = this.table;
    if (activeRoundId === null || localPlayerId === null) return ClientTable.notReady('no round or no identity');
    const request: IPayloadAnnounce = { roundId: activeRoundId, playerId: localPlayerId, score, blind: !this.table.diceRevealed };
    return allowed(request);
  }

  /** The claim stands: the server's decider, the score, whether it went blind; everyone now decides. */
  claimAccepted(command: ICommandClaimAccepted): boolean {
    const { request, answer } = command;
    if (!this.seatsAs(request.playerId)) return false;
    this.adoptDecider(answer.challengerId);
    this.table.lastClaimedScore = request.score;
    this.table.claimWasBlind = answer.wasBlind ?? request.blind;
    this.enter(GamePhase.DECIDING);
    return true;
  }

  /** Believe: the next roller must beat the believed score — unless the claim is Meyer, which is only lifted. */
  planBelieve(ranking: DiceRanking): TDecisionResult<IPayloadBelieve> {
    const { activeRoundId, localPlayerId, room } = this.table;
    if (activeRoundId === null || localPlayerId === null || room === null) return ClientTable.notReady('no round, room or identity');
    const claim = this.claim();
    if (claim !== null && !claim.canBeBelieved(ranking)) {
      return refuse('Meyer cannot be believed — only lifted.', REFUSAL.MEYER_CANNOT_BE_BELIEVED);
    }
    const request: IPayloadBelieve = {
      roundId: activeRoundId,
      playerId: localPlayerId,
      roomId: room.roomId,
      minimumScore: claim === null ? 0 : claim.score,
    };
    return allowed(request);
  }

  believeAccepted(command: ICommandBelieveAccepted): boolean {
    const { request, answer } = command;
    if (!this.seatsAs(request.playerId) || !this.sitsAt(request.roomId)) return false;
    this.table.activeRoundId = answer.roundId ?? null;
    this.table.activeRollerId = answer.rollerId ?? null;
    this.table.minimumClaimScore = answer.minimumScore ?? request.minimumScore;
    this.table.lastDiceRoll = null;
    this.table.opponentClaim = null;
    this.table.activeChallengerId = null;
    this.enter(GamePhase.ROLLING);
    return true;
  }

  /** Roll First: the loser opens the next round with a clean floor (0 = any valid claim). */
  planRollFirst(): TDecisionResult<IPayloadBelieve> {
    const { activeRoundId, localPlayerId, room } = this.table;
    if (activeRoundId === null || localPlayerId === null || room === null) return ClientTable.notReady('no round, room or identity');
    const request: IPayloadBelieve = { roundId: activeRoundId, playerId: localPlayerId, roomId: room.roomId, minimumScore: 0 };
    return allowed(request);
  }

  /** Like a believe, but with no floor and without clearing the server's decider — the handler did not. */
  rollFirstAccepted(command: ICommandBelieveAccepted): boolean {
    const { request, answer } = command;
    if (!this.seatsAs(request.playerId) || !this.sitsAt(request.roomId)) return false;
    this.table.activeRoundId = answer.roundId ?? null;
    this.table.activeRollerId = answer.rollerId ?? null;
    this.table.minimumClaimScore = 0;
    this.table.lastDiceRoll = null;
    this.table.opponentClaim = null;
    this.enter(GamePhase.ROLLING);
    return true;
  }

  /** Lift: the server's decider is cleared BEFORE the call; the reveal arrives as the cup-lift event. */
  planLift(): TDecisionResult<IPayloadRoundMove> {
    const { activeRoundId, localPlayerId } = this.table;
    if (activeRoundId === null || localPlayerId === null) return ClientTable.notReady('no round or no identity');
    const request: IPayloadRoundMove = { roundId: activeRoundId, playerId: localPlayerId };
    this.table.activeChallengerId = null;
    return allowed(request);
  }

  /** The lift came back — answered or refused, the table moved; nothing on it changes here. */
  liftAnswered(command: ICommandRoundMoveAnswered): boolean {
    return this.seatsAs(command.request.playerId);
  }

  /** Play Again (after the game): every seat back to full lives BEFORE the call; the last loser rolls first. */
  planPlayAgain(): TDecisionResult<IPayloadPlayAgain> {
    const { room, revealResult } = this.table;
    if (room === null || revealResult === null) return ClientTable.notReady('no room or no reveal');
    const request: IPayloadPlayAgain = { roomId: room.roomId, rollerId: revealResult.losingPlayerId };
    this.restoreLives();
    return allowed(request);
  }

  /**
   * The table moved either way. If the server opened the round and the local player is the new roller, they move
   * to ROLLING on the answer instead of waiting for the round's start event — otherwise the round id can be
   * missing when they tap the cup, and the roll does nothing.
   */
  playAgainAnswered(command: ICommandPlayAgainAnswered): boolean {
    const { request, answer } = command;
    if (!this.sitsAt(request.roomId)) return false;
    if (answer !== null && answer.roundId && this.table.localPlayerId === request.rollerId) {
      this.table.activeRoundId = answer.roundId;
      this.table.activeRollerId = answer.rollerId ?? null;
      this.table.minimumClaimScore = 0;
      this.table.lastDiceRoll = null;
      this.enter(GamePhase.ROLLING);
    }
    return true;
  }

  /**
   * Leave the table: everything back to the store's initial state — `resetAll`, which also forgets the identity
   * until the identity flows restore it. Answers the seat to give up when the server is to be told, which is only
   * from the exit button: the room-ended overlay's "back to menu" has nobody to tell, the server closed the table.
   */
  leave(notifyServer: boolean): IEntityLeave['leave'] {
    const { room, localPlayerId } = this.table;
    const seat: IEntityLeave['leave'] = notifyServer && room !== null && localPlayerId !== null
      ? { roomId: room.roomId, playerId: localPlayerId }
      : null;
    this.table = { ...EMPTY_TABLE };
    return seat;
  }

  // ── The server's events ────────────────────────────────────────────────────────────────────────────────
  // Before FE1 each was a subscription callback in a component or hook: ControlPanel (claim, lift, round, roster),
  // GameSceneCore (roll), LobbyPanel's two sync hooks (round, roster), `useRoomSubscriptions` (presence,
  // abandoned, countdown) and `useWsConnectionSync` (connectivity). Each callback's guards are kept, including the
  // ones that only existed because a subscription was mounted or not: "the roller never subscribes to claims"
  // became "a claim is ignored on the roller's table".

  /**
   * A round started — the game's first, or the next after a believe, a roll-first or Play Again; what it does is
   * the phase's (the lobby's first round, a new game after game over, the next round). The room check is a second
   * line of defence behind the server's per-room topic (version skew mid-deploy).
   */
  roundStarted(round: IPayloadRoundStarted): boolean {
    const room = this.table.room;
    if (room === null || round.roomId !== room.roomId) return false;
    this.phase.roundStarted(this.table, round, room);
    return true;
  }

  /**
   * A claim was announced. Every seat but the roller's tracks it, so every table moves to DECIDING; only the
   * challenger's shows the buttons. The SERVER'S decider is adopted, so a stale local roster can no longer
   * disenfranchise or mis-crown one.
   */
  claimAnnounced(claim: IPayloadClaimAnnounced): boolean {
    if (!this.isOnRound(claim.roundId) || this.isLocalRoller()) return false;
    this.table.lastClaimedScore = claim.claimedScore;
    this.table.claimWasBlind = claim.wasBlind === true;
    this.table.opponentClaim = Claim.announced(claim.claimedScore, claim.label).toSnapshot();
    this.adoptDecider(claim.challengerId);
    this.enter(GamePhase.DECIDING);
    return true;
  }

  /**
   * The cup was lifted. The loser's lives are the table's count when the server sends it (backend §28.191), and are
   * decremented locally only for an older server's reveal. The dice go on screen for every seat, and the game ends
   * when the server says so — or, from an older server, when the loser has no lives left. A lift for a round
   * already finished must not touch the lives.
   */
  cupLifted(result: IPayloadCupLifted, ranking: DiceRanking): boolean {
    if (!this.isOnRound(result.roundId)) return false;
    const lives = TableLives.of(this.table.playerLives);
    const left = lives.leftAfterLosing(result.losingPlayerId, result.loserLivesLeft);
    this.table.playerLives = lives.with(result.losingPlayerId, left).toRecord();
    this.table.minimumClaimScore = 0;
    const reveal = Reveal.of(result);
    if (reveal.hasDice()) this.table.lastDiceRoll = reveal.diceRoll(ranking);
    this.table.revealResult = reveal.stored();
    this.enter(reveal.phaseAfterLift(left));
    return true;
  }

  /** The roller rolled. Every other seat watches the dice being peeked at; a stale round's roll is dropped. */
  diceRolled(ev: IPayloadDiceRolled): boolean {
    if (!this.isOnRound(ev.roundId) || this.isLocalRoller()) return false;
    this.enter(GamePhase.PEEKING);
    return true;
  }

  /** Someone joined or left: the roster as the phase takes it — rebuilt in the lobby, merged in a game. */
  roomUpdated(updated: IPayloadRoomRoster): boolean {
    const room = this.table.room;
    if (room === null || updated.id !== room.roomId) return false;
    const seats = this.phase.rosterAfter(Roster.of(room.players), updated.players, this.table.localPlayerId);
    this.table.room = { ...room, players: seats.toSnapshot() };
    return true;
  }

  /**
   * A player was suspended or resumed, in THIS room. A logged-in account keeps its id across rooms, so a previous
   * room's suspension of the same person must not badge them here — the room check stays even though the server
   * scopes the topic (§23.26).
   */
  private concernsThisRoom(ev: IPayloadPresence): ev is IPayloadPresence & { readonly playerId: string } {
    return this.table.room !== null && Boolean(ev.playerId) && ev.roomId === this.table.room.roomId;
  }

  playerSuspended(ev: IPayloadPresence): boolean {
    if (!this.concernsThisRoom(ev)) return false;
    if (!this.table.suspendedPlayerIds.includes(ev.playerId)) {
      this.table.suspendedPlayerIds = [...this.table.suspendedPlayerIds, ev.playerId];
    }
    return true;
  }

  playerResumed(ev: IPayloadPresence): boolean {
    if (!this.concernsThisRoom(ev)) return false;
    const playerId = ev.playerId;
    this.table.suspendedPlayerIds = this.table.suspendedPlayerIds.filter((id) => id !== playerId);
    return true;
  }

  /** The server closed this table: the room-ended overlay. */
  roomAbandoned(ev: IPayloadRoomAbandoned): boolean {
    if (!ev.roomId || !this.sitsAt(ev.roomId)) return false;
    this.table.roomEnded = true;
    return true;
  }

  /** The server's auto-believe clock ticked. Taken as sent; a round's start clears it. */
  countdownTicked(ev: IPayloadDecisionCountdown): boolean {
    if (this.table.room === null) return false;
    this.table.decisionCountdown = { roundId: ev.roundId, challengerId: ev.challengerId, secondsRemaining: ev.secondsRemaining };
    return true;
  }

  /**
   * The websocket changed state; answers whether a resync is owed. The LATCH is the point (`useWsConnectionSync`):
   * a resync is owed when a connection is RE-established, never when it is first established — and anything that
   * is not a live connection counts as a gap. An unknown state shows as disconnected but is not a gap. The latch
   * lived in a ref because an effect-local variable was reset by every re-render (found by the SUS-DEC e2e cells);
   * on the table it survives anything short of `resetAll`.
   */
  connectionChanged(state: string): boolean {
    const wasDown = this.table.connectionWasDown;
    this.table.connectionState = isMapped(state) ? state : 'disconnected';
    if (DOWN_STATES.includes(state)) {
      this.table.connectionWasDown = true;
      return false;
    }
    if (state === 'connected' && wasDown) {
      this.table.connectionWasDown = false;
      return true;
    }
    return false;
  }

  // ── A seat taken: what the session's moves write at the table (frontend-domain-oo P4) ───────────────
  // The identity flows and the seating flows were the session draft's writes into the table (`withPlayer`, `seated`,
  // `inLobby`, `decisions/session/sessionDraft.ts`); `ClientSession` makes them, here.

  /** Seated: the table has a room. */
  isSeated(): boolean {
    return this.table.room !== null;
  }

  /**
   * The tab plays as `playerId`. One identity, two views: the table's `localPlayerId` and the session's player are the
   * same store field (`localUser`), and the session changes them together.
   */
  identifyAs(playerId: string): void {
    this.table.localPlayerId = playerId;
  }

  /** A seat taken at `room`. */
  sitAt(room: TableRoom): void {
    this.table.room = room.toSnapshot();
  }

  /** A seat at a table that has not started: the lobby. */
  enterLobby(): void {
    this.enter(GamePhase.LOBBY);
  }

  /** Every seat the server lists, at full lives — and nobody else. */
  livesFor(playerIds: ReadonlyArray<string>): void {
    this.table.playerLives = TableLives.full(playerIds).toRecord();
  }

  // ── Catching up: a pushed view, a reconnect's resync, the lobby's reads ─────────────────────────────────

  /**
   * THE ACTIVE ROUND ONTO THE TABLE — `applyActiveRoundSnapshot` (application/use-cases/useGameStateRestore.ts),
   * moved in FE1, as `playerId` sees it. Lives first, whatever the phase. Then the reveal, the pending claim, or the
   * round's phase:
   *
   *   - no round at all: between rounds — sit in ROLLING and catch the next round's start;
   *   - a reveal: on screen for everyone, REVEALING only for the loser (`Reveal.phaseOnReturn`);
   *   - a pending claim: DECIDING, with the server's decider and whether the claim went blind (§28.61), and the
   *     claim itself only on the decider's own table;
   *   - dice rolled by this roller: PEEKING; anything else: ROLLING.
   *
   * Two of the store's steps happen here in one. A reveal used to wait in `pendingRevealResult` until ControlPanel's
   * effect copied it to the screen, with the reveal's dice, and only if no reveal was showing already. A pending
   * claim waited the same way in `pendingOpponentClaim`. Both are written directly now: a newer reveal replaces the
   * one on screen instead of queueing behind it. The queued copy was replayed by the next round's start, which then
   * put the previous reveal back on screen (deviation, flagged in §28).
   *
   * Every seat gets full lives when the server lists none. `playerId` is compared as the callers did: the
   * reconnect passes the player's id, the pushed view `localPlayerId ?? ''`, the session's restore the request's
   * player — it runs before `me` may have answered. The reveal's dice are classified by the ranking the table is
   * played by (frontend-domain-oo P2).
   */
  restoreRound(ar: IPayloadActiveRound | null, playerId: string, ranking: DiceRanking): void {
    this.table.playerLives = (ar !== null && ar.playerLives.length > 0
      ? TableLives.counted(ar.playerLives)
      : TableLives.full(this.seats().ids())).toRecord();
    if (ar === null) {
      this.enter(GamePhase.ROLLING);
      return;
    }
    if (ar.revealResult !== null) {
      const reveal = Reveal.of(ar.revealResult);
      this.table.revealResult = reveal.stored();
      this.table.lastDiceRoll = reveal.diceRoll(ranking);
      this.enter(reveal.phaseOnReturn(playerId));
      return;
    }
    this.table.activeRoundId = ar.roundId;
    this.table.activeRollerId = ar.rollerId;
    this.table.minimumClaimScore = ar.minimumScore ?? 0;
    const pending = ar.pendingClaim;
    if (pending !== null) {
      this.table.lastClaimedScore = pending.claimedScore;
      this.table.claimWasBlind = pending.wasBlind;
      this.adoptDecider(pending.challengerId);
      if (pending.challengerId === playerId) this.table.opponentClaim = Claim.announced(pending.claimedScore, pending.label).toSnapshot();
      this.enter(GamePhase.DECIDING);
    } else if (ar.diceRolled && ar.rollerId === playerId) {
      this.enter(GamePhase.PEEKING);
    } else {
      this.enter(GamePhase.ROLLING);
    }
  }

  /**
   * THE TABLE CORRECTS THE CLIENT — `reconcileTableView` (application/use-cases/reconcileTableView.ts) and
   * `useTableView`'s version bookkeeping, moved in FE1 (backend §28.192). The server pushes the whole table after
   * every save, AFTER the move's own events. A view for another room is ignored; one at or below the version
   * already applied is stale — two replicas can deliver out of order. Otherwise:
   *
   *   - FACTS always: each seat's lives, and who is suspended — idempotent to set;
   *   - a CLOSED table the client still plays at: the room has ended (not in the lobby);
   *   - a MISSED MOVE only on divergence: a live round the client does not have, a claim it has not seen, or a
   *     reveal while it shows none.
   *
   * When the client heard the move's event — the normal case, since the view comes after it — nothing but the
   * facts is touched: the phases the events drive stay theirs.
   */
  tableView(view: IPayloadTableView, ranking: DiceRanking): IEntityTableViewOutcome['outcome'] {
    const room = this.table.room;
    if (room === null || view.roomId !== room.roomId) return 'ignored';
    const held = this.table.tableVersion;
    const lastVersion = held !== null && held.roomId === view.roomId ? held.version : 0;
    if (view.version <= lastVersion) return 'stale';

    this.table.tableVersion = { roomId: view.roomId, version: view.version };
    const ar = view.activeRound;
    if (ar !== null && ar.playerLives.length > 0 && TableLives.of(this.table.playerLives).differsFrom(ar.playerLives)) {
      this.table.playerLives = TableLives.counted(ar.playerLives).toRecord();
    }
    if (!sameSet(view.suspendedPlayerIds, this.table.suspendedPlayerIds)) this.table.suspendedPlayerIds = [...view.suspendedPlayerIds];

    if (view.status === 'FINISHED') {
      if (!this.phase.isLobby()) this.table.roomEnded = true;
      return 'reconciled';
    }
    if (ar === null) return 'in-sync';

    const missedRound = ar.roundId !== null && ar.roundId !== this.table.activeRoundId;
    const missedClaim = ar.roundId !== null && ar.pendingClaim !== null && ar.pendingClaim.claimedScore !== this.table.lastClaimedScore;
    const missedReveal = ar.roundId === null && ar.revealResult !== null && !this.phase.showsTheReveal();
    if (!missedRound && !missedClaim && !missedReveal) return 'in-sync';

    if (missedReveal && ar.revealResult !== null) {
      const reveal = Reveal.of(ar.revealResult);
      this.table.revealResult = reveal.stored();
      this.table.lastDiceRoll = reveal.diceRoll(ranking);
      this.enter(reveal.phaseWhenMissed());
      return 'reconciled';
    }
    this.restoreRound(ar, this.table.localPlayerId ?? '', ranking);
    return 'reconciled';
  }

  /**
   * A RECONNECT'S CATCH-UP — `useResyncActiveRound`, moved in FE1, in three decisions around two reads. A socket that
   * comes back delivers NEW events, but the moves others made while this tab was away are never replayed, so the
   * active round is read and mapped back on, silently, and then the room's suspended list, which live events
   * maintain only additively — exactly the events a drop can miss (§23.26). Only a round in play is caught up.
   */
  planResync(): TDecisionResult<IPayloadResync> {
    const room = this.table.room;
    if (room === null || this.table.localPlayerId === null) return refuse('No table to catch up.', REFUSAL.NOT_READY);
    if (!this.phase.hasARoundToCatchUp()) return refuse(`Nothing to catch up in ${this.table.phase}.`, REFUSAL.NOT_IN_PLAY);
    const request: IPayloadResync = { roomId: room.roomId, roomCode: room.roomCode };
    return allowed(request);
  }

  /**
   * The active round, read on reconnect, mapped on — and the decision countdown cleared: it belongs to whatever
   * round last ticked it, a reconnect may have missed both its last tick and the round's change, and a still-live
   * countdown refills within a second.
   *
   * Only a read that ANSWERED is applied. The hook mapped a failed read as "no round" — every seat back to six
   * lives, the phase to ROLLING — until the next pushed view corrected it; the shell now skips a failed read, as
   * the hook skipped a thrown one (deviation, flagged in §28). An answer for a table the player has since left is
   * ignored.
   */
  activeRoundRestored(command: ICommandActiveRoundRestored, ranking: DiceRanking): boolean {
    const me = this.table.localPlayerId;
    if (!this.sitsAt(command.request.roomId) || me === null) return false;
    this.restoreRound(command.activeRound, me, ranking);
    this.table.decisionCountdown = null;
    return true;
  }

  /** The room's own list of who is suspended replaces the additive one; nothing when the room is gone. */
  suspendedReconciled(command: ICommandSuspendedReconciled): boolean {
    if (!this.sitsAt(command.request.roomId)) return false;
    if (command.suspendedPlayers === null || command.suspendedPlayers === undefined) return false;
    this.table.suspendedPlayerIds = command.suspendedPlayers.filter((p) => !p.isConnected).map((p) => p.id);
    return true;
  }

  /**
   * THE LOBBY'S READS — the roster on entry (`useLobbyRoomSync`) and the self-heal poll for a round that started
   * unheard (`useLobbyActiveRoundPoll`, every 2.5 s). Both ran only while LobbyPanel was mounted, and dropped an
   * answer that came back after it unmounted; here, after the table left the lobby.
   */
  planLobbyRead(): TDecisionResult<IPayloadRoomRead> {
    const room = this.table.room;
    if (room === null) return refuse('No room to read.', REFUSAL.NOT_READY);
    if (!this.phase.isLobby()) return refuse('The table has left the lobby.', REFUSAL.NOT_IN_LOBBY);
    const request: IPayloadRoomRead = { roomId: room.roomId };
    return allowed(request);
  }

  /** A round started that the lobby did not hear: roller, round, full lives, ROLLING. */
  lobbyActiveRound(command: ICommandLobbyActiveRound): boolean {
    const room = this.table.room;
    if (room === null || room.roomId !== command.request.roomId || !this.phase.isLobby()) return false;
    const ar = command.activeRound;
    if (ar === null || !ar.roundId) return false;
    this.table.activeRoundId = ar.roundId;
    this.table.activeRollerId = ar.rollerId;
    if (room.players.length > 0) this.restoreLives();
    this.enter(GamePhase.ROLLING);
    return true;
  }

  /** The lobby's roster on entry: every seat fresh from the server's list. */
  lobbyRoomLoaded(command: ICommandLobbyRoomLoaded): boolean {
    const room = this.table.room;
    if (room === null || room.roomId !== command.request.roomId || !this.phase.isLobby()) return false;
    if (command.roster.id !== room.roomId) return false;
    this.table.room = { ...room, players: Roster.fresh(command.roster.players, this.table.localPlayerId).toSnapshot() };
    return true;
  }

  // ── Queries ────────────────────────────────────────────────────────────────────────────────────────────

  /**
   * THE TABLE'S DERIVED VALUES — what the screen needs from the table that only the game's rules can say
   * (`deriveTable`, FE1, until P3; each was an expression a component computed inline):
   *
   *   - `challengerId`: AppRoot's `activeChallengerId ?? getChallengerId(…)` — the server's decider, or the
   *     rotation (`Roster.challengerAfter`). ControlPanel computed the same at its single use.
   *   - `blockingOpponentId`: AppRoot's `resolveBlockingSuspendedOpponentId(…)` over that challenger.
   *   - the claim lists: ControlPanel's two pickers. The blind picker lists every claim over the floor. The open
   *     picker splits them at the rolled score, but only when there is a floor to beat; the first claim of a game
   *     may be anything.
   *   - `cupAction`: GameSceneCore's `handleCupTap` dispatch, including its `!activeRoundId` bail-out. The scenes'
   *     `canTap` omitted that check, so a cup without a round glowed and invited a tap that did nothing; they read
   *     this now (deviation, flagged in §28 for FE1).
   *
   * THE CLAIMS, LABELS AND DICE ARE THE SERVER'S (frontend-domain-oo P2): every one is read from the ranking the table
   * is played by, which lists no claim and names no combination until it has arrived.
   */
  derived(ranking: DiceRanking): IEntityClientTableDerived {
    const t = this.table;
    const me = t.localPlayerId;
    const challengerId = t.activeChallengerId
      ?? this.seats().challengerAfter(t.activeRollerId, TableLives.of(t.playerLives), t.suspendedPlayerIds);
    const isActiveRoller = this.isLocalRoller();
    const rolled = t.lastDiceRoll === null ? null : t.lastDiceRoll.score;
    const option = (rank: IEntityClientDiceRank): IEntityClientClaimOption => ({ score: rank.score, label: rank.label, isRolled: rank.score === rolled });
    // A claim must strictly BEAT the floor — matching it is not a legal claim.
    const legalClaims = ranking.beating(t.minimumClaimScore).map(option);
    // With the dice seen and a floor to beat: below the rolled score is a bluff, the rest is honest.
    const tacticalClaims = rolled !== null && t.minimumClaimScore > 0 ? legalClaims.filter((c) => c.score < rolled) : [];
    const honestClaims = rolled !== null && t.minimumClaimScore > 0 ? legalClaims.filter((c) => c.score >= rolled) : legalClaims;
    const claim = this.claim();

    const derived: IEntityClientTableDerived = {
      challengerId,
      isChallenger: me !== null && me === challengerId,
      isActiveRoller,
      blockingOpponentId: this.blockingOpponent(challengerId),
      cupAction: isActiveRoller && t.activeRoundId !== null ? this.phase.cupAction(t.diceRevealed) : null,
      legalClaims,
      tacticalClaims,
      honestClaims,
      rolledLabel: rolled === null ? null : ranking.labelOf(rolled),
      opponentClaimDice: claim === null ? null : claim.dice(ranking),
      canBelieve: claim !== null && claim.canBeBelieved(ranking),
      revealLabels: t.revealResult === null
        ? null
        : { claimed: ranking.labelOf(t.revealResult.claimedScore), actual: ranking.labelOf(t.revealResult.trueScore) },
    };
    return derived;
  }

  /**
   * The suspended opponent who is currently BLOCKING the table — the player the phase waits on, if they happen to be
   * suspended and are not this tab's (`resolveBlockingSuspendedOpponentId`, moved to the Domain in FE1 from
   * application/use-cases/resolveBlockingSuspension.ts).
   *
   * Nobody when the awaited player is NOT suspended, so an incidental drop by a passive player (common in 3-4 player
   * games, or on a single device where only one tab can be foreground) never freezes everyone else — they show as
   * SUSPENDED in the roster while play continues. The server already auto-advances when the awaited player drops
   * (SKIP_ROLLER for a suspended roller, AUTO_BELIEVE for a suspended challenger), after which that player is no
   * longer awaited and this answers nobody again.
   */
  private blockingOpponent(challengerId: string | null): string | null {
    const awaited = this.phase.awaitedPlayer(this.table.activeRollerId, challengerId);
    if (!awaited || awaited === this.table.localPlayerId) return null;
    return this.table.suspendedPlayerIds.includes(awaited) ? awaited : null;
  }

  /**
   * The game this tab remembers across a reload: the seat, with the code the player sees — or, for a room without
   * one, the first four characters of its id, as the persistence effect derived it. None without both a room and a
   * player.
   */
  resume(): IEntityClientResume | null {
    const { room, localPlayerId } = this.table;
    if (room === null || localPlayerId === null) return null;
    return {
      playerId: localPlayerId,
      roomId: room.roomId,
      roomCode: room.roomCode || room.roomId.slice(0, 4).toUpperCase(),
    };
  }

  /**
   * THE STORED GAME FOLLOWS THE SEAT (frontend strict-ports plan, FE2, 2026-10-03). The two rules
   * `useResumeSessionPersistence` applied by watching the store, applied where the seat changes instead:
   *
   *   - a seat that differs from the one `before` had (another room, another code, another player) is stored;
   *   - a table that moves INTO idle forgets it. Only a transition does: an idle table that stays idle leaves the
   *     record alone, which is what let a refreshed player find their game on the way back.
   *
   * Undefined is "leave the record as it is". A reset is read against the table BEFORE it, so leaving a game
   * forgets it.
   */
  storedGameSince(before: ClientTable): IEntityClientResume | null | undefined {
    const seat = this.resume();
    if (seat !== null && !sameJson(before.resume(), seat)) return seat;
    if (this.phase.isIdle() && !before.phase.isIdle()) return null;
    return undefined;
  }
}

function sameSet(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}
